<script lang="ts">
  /**
   * The shell: which screen is open, how much is solved, and the shortcuts.
   *
   * The shortcuts stand aside for anything CodeMirror owns. While the caret is
   * in an editor, `[` and `]` are text and Ctrl/Cmd + arrow keys move the caret,
   * so those keys only mean "previous/next level" outside one; Ctrl/Cmd + Enter
   * is handled by the level itself there.
   */
  import Library from './components/Library.svelte';
  import LevelView from './components/LevelView.svelte';
  import Progress from './components/Progress.svelte';
  import Sandbox from './components/Sandbox.svelte';
  import Sidebar from './components/Sidebar.svelte';
  import { nextLevelId, previousLevelId } from './content';
  import { game } from './state/game.svelte';
  import { holdCaret } from './lib/focus';
  import { theme } from './state/theme.svelte';

  let drawerOpen = $state(false);

  const solvedCount = $derived(game.solvedCount());
  const stale = $derived(game.staleLevels());

  /** Whether a keystroke belongs to the thing the player is typing into. */
  function isTyping(target: EventTarget | null): boolean {
    return (
      target instanceof HTMLElement &&
      target.closest('.cm-editor, input, textarea, select, [contenteditable="true"]') !== null
    );
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.defaultPrevented || event.altKey) {
      return;
    }
    if (isTyping(event.target)) {
      return;
    }
    if (event.key === 'Escape' && drawerOpen) {
      drawerOpen = false;
    } else if (event.key === '[') {
      void game.openLevel(previousLevelId(game.activeLevelId));
    } else if (event.key === ']') {
      void game.openLevel(nextLevelId(game.activeLevelId));
    }
  }
</script>

<svelte:window onkeydown={onKeydown} />

<div class="app">
  <header class="top">
    <button
      class="drawer-toggle"
      aria-label="Toggle the level list"
      aria-expanded={drawerOpen}
      aria-controls="level-list"
      onclick={() => (drawerOpen = !drawerOpen)}
    >
      ☰
    </button>
    <!-- The mark the game is named after, in the colour the editor gives a
         lambda, and the full stop in the colour it gives the dot that ends a
         lambda's parameters, so the wordmark and the code agree. -->
    <span class="brand"><span class="mark">\</span> Lambda Game<span class="dot">.</span></span>
    <nav class="tabs">
      <button class:active={game.view === 'level'} onclick={() => (game.view = 'level')}>Level</button>
      <button class:active={game.view === 'library'} onclick={() => (game.view = 'library')}>
        Library
      </button>
      <button class:active={game.view === 'sandbox'} onclick={() => (game.view = 'sandbox')}>
        Sandbox
      </button>
    </nav>
    <Progress />
    {#if !game.progress && stale.length > 0}
      <button
        class="stale"
        title="Solved before your definitions changed"
        onmousedown={holdCaret}
        onclick={() => void game.recheckStale()}
      >
        ↻ {stale.length} to re-check
      </button>
    {/if}
    <span class="score">{solvedCount} solved</span>
    <button
      class="theme"
      title={theme.title}
      aria-label={theme.title}
      onmousedown={holdCaret}
      onclick={() => theme.toggle()}
    >
      {#if theme.palette === 'light'}
        <!-- A sun: a disc with rays, in the same box as the moon. -->
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="8" r="3.1" />
          <path
            d="M8 1.2v1.6M8 13.2v1.6M1.2 8h1.6M13.2 8h1.6M3.2 3.2l1.1 1.1M11.7 11.7l1.1 1.1M12.8 3.2l-1.1 1.1M4.3 11.7l-1.1 1.1"
          />
        </svg>
      {:else}
        <!--
          A moon: the crescent from the Lucide set (ISC licence), scaled from
          its 24 unit box to this one, turned to face the other way and with
          the disc cut out of it smaller so the crescent is a little thinner.
        -->
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M8 2a4 4 0 0 1-6 6 6 6 0 1 0 6-6Z" />
        </svg>
      {/if}
    </button>
  </header>

  <div class="body">
    <aside class:open={drawerOpen} id="level-list">
      <Sidebar onselect={() => (drawerOpen = false)} />
    </aside>
    {#if drawerOpen}
      <button
        class="scrim"
        aria-label="Close the level list"
        onclick={() => (drawerOpen = false)}
      ></button>
    {/if}
    <main>
      {#if game.view === 'level'}
        <LevelView />
      {:else if game.view === 'library'}
        <Library />
      {:else}
        <Sandbox />
      {/if}
    </main>
  </div>
</div>

<style>
  .app {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .top {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 12px;
    height: 46px;
    border-bottom: 1px solid var(--border);
    background: var(--bg-raised);
    flex: none;
  }

  .brand {
    font-weight: 600;
    white-space: nowrap;
  }

  .brand .mark {
    color: var(--code-keyword);
  }

  .brand .dot {
    color: var(--code-punct);
  }

  .tabs {
    display: flex;
    gap: 4px;
    margin-left: 8px;
  }

  .tabs button {
    background: none;
    border: 1px solid transparent;
    color: var(--text-dim);
    padding: 4px 10px;
  }

  .tabs button.active {
    color: var(--text);
    background: var(--bg-input);
    border-color: var(--border-strong);
  }

  .stale {
    padding: 3px 9px;
    font-size: 12px;
    background: none;
    white-space: nowrap;
    color: var(--warn);
    border-color: var(--warn);
  }

  .score {
    margin-left: auto;
    color: var(--text-faint);
    font-size: 12px;
    white-space: nowrap;
  }

  /*
   * The mark is drawn rather than set as a character: a sun and a moon are
   * different glyphs with different shapes inside their boxes, so text put one
   * of them lower and left of the other however the button was aligned. Two
   * drawings of the same size, in the same box, cannot drift.
   */
  .theme {
    flex: none;
    display: grid;
    place-items: center;
    width: 30px;
    height: 26px;
    background: none;
    border-color: transparent;
    color: var(--text-dim);
    padding: 0;
  }

  .theme svg {
    width: 15px;
    height: 15px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.4;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .theme:hover:not(:disabled) {
    color: var(--text);
    border-color: var(--border-strong);
  }

  .drawer-toggle {
    display: none;
    background: none;
    border: none;
    font-size: 18px;
    padding: 0 4px;
  }

  .body {
    flex: 1;
    display: flex;
    min-height: 0;
    /*
     * The drawer and its scrim are placed against this box rather than the
     * window, so they start under the header whatever height it has.
     */
    position: relative;
  }

  aside {
    width: 240px;
    flex: none;
    border-right: 1px solid var(--border);
    background: var(--bg);
  }

  main {
    flex: 1;
    overflow-y: auto;
    min-width: 0;
  }

  .scrim {
    display: none;
  }

  @media (max-width: 720px) {
    .drawer-toggle {
      display: block;
    }

    /*
     * The closed drawer is off the screen and out of the way entirely: without
     * `visibility`, its buttons would still take focus and Tab would walk
     * through a list the user cannot see.
     */
    aside {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      z-index: 20;
      transform: translateX(-100%);
      visibility: hidden;
      transition:
        transform 0.16s ease-out,
        visibility 0s 0.16s;
      box-shadow: 0 0 24px rgb(0 0 0 / 40%);
    }

    aside.open {
      transform: translateX(0);
      visibility: visible;
      transition: transform 0.16s ease-out;
    }

    .scrim {
      display: block;
      position: absolute;
      inset: 0;
      z-index: 10;
      background: rgb(0 0 0 / 35%);
      border: none;
      border-radius: 0;
    }
  }

  /*
   * A phone cannot hold the wordmark, three tabs, the count and the theme
   * control in one row, so the row folds in two: the mark and the counts on
   * top, the tabs across the bottom, each of them the full width of its third
   * of the bar, which is also a comfortable thing to tap.
   */
  @media (max-width: 560px) {
    .top {
      height: auto;
      flex-wrap: wrap;
      padding: 6px 10px;
      row-gap: 6px;
      gap: 8px;
    }

    .brand {
      flex: 1;
    }

    .tabs {
      order: 1;
      width: 100%;
      margin-left: 0;
    }

    .tabs button {
      flex: 1;
      padding: 8px 10px;
    }

    .theme {
      width: 38px;
      height: 34px;
    }

    .drawer-toggle {
      padding: 0 8px;
    }
  }
</style>
